import { memo, useMemo, useState } from "react";
import { DeleteOutlined, UserOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, AutoComplete, Button, Card, Col, DatePicker, Form, Input, InputNumber, message, Modal, Row, Select, Space, Switch, Typography } from "antd";
import dayjs from "dayjs";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { isSameYear } from "../../../../variables/functions";
import { computeImmatriculation } from "./data/donnees";
import { LOGGED_USER } from "../../../../variables/constants";
import { useParams } from "react-router-dom";
const { Option } = Select;

const EnginForm = ({ index, marques, carosseries, genres = [], form, removeVehicle, openConducteurModal, garantiesNonObligatoires = [], onEnginsUpdate, setSameYear, categorie, editMode }) => {
    const param = useParams();
    const { Text } = Typography;
    const [organismeModal, setOrganismeModal] = useState({
        visible: false,
        data: null,
        field: null, // 'immatriculation' ou 'chassis'
        value: null
    });

    const getEngins = () => form.getFieldValue('engins') || [];
    const engins = getEngins();
    const engin = engins[index] || {};
    const normalizedMarques = useMemo(
        () => (marques || [])
            .map((marque) => ({
                ...marque,
                nom: marque?.nom || marque?.libelleParametre,
                marqueId: marque?.marqueId,
            }))
            .filter((marque) => marque.nom),
        [marques]
    );
    const selectedMarque = normalizedMarques.find(
        (marque) => (marque.nom || "").toUpperCase() === (engin.marque || "").toUpperCase()
    );
    const modeleOptions = (selectedMarque?.modeles || [])
        .filter((modele) => modele?.nom)
        .map((modele) => ({ label: modele.nom, value: modele.nom }));

    // Vérifier si châssis existe dans la BD
    const checkChassisInDB = async (chassis) => {
        if (!chassis) return false;
        if (editMode) return false;
        try {
            const response = await axiosInstance.get(`/api/engin/chassis/${chassis}`);
            return response.data || false;
        } catch (error) {
            console.error("Erreur lors de la vérification du châssis:", error);
            return false;
        }
    };

    // Vérifier l'organisme du contrat lié à l'engin
    const checkOrganismeContrat = async (enginId) => {
        try {
            const response = await axiosInstance.get(`/api/contrat/engin/${enginId}/organisme`);
            // Retourne { existe: true/false, organisme: "NOM_ORGANISME", dateDebut: "2023-01-01", dateFin: "2024-01-01" }
            return response.data;
        } catch (error) {
            console.error("Erreur lors de la vérification de l'organisme:", error);
            return null;
        }
    };

    // Vérifier si immatriculation existe déjà dans les engins précédents
    const checkImmatriculationInEngins = (immatriculation, currentIndex) => {
        const allEngins = getEngins();
        return allEngins.some((eng, idx) =>
            idx !== currentIndex &&
            eng.immatriculation &&
            eng.immatriculation.toLowerCase() === immatriculation.toLowerCase()
        );
    };

    // Vérifier si châssis existe déjà dans les engins précédents
    const checkChassisInEngins = (chassis, currentIndex) => {
        const allEngins = getEngins();
        return allEngins.some((eng, idx) =>
            idx !== currentIndex &&
            eng.numeroChassis &&
            eng.numeroChassis.toLowerCase() === chassis.toLowerCase()
        );
    };

    // Gérer la confirmation de l'utilisateur pour continuer malgré l'existence dans un autre organisme
    const handleOrganismeConfirm = async () => {
        const { data, field, value } = organismeModal;

        try {
            // Récupérer les détails complets de l'engin depuis le backend (par immatriculation OU châssis)
            const enginsExistants = Array.isArray(organismeModal.data?.engins) ? organismeModal.data.engins : [];
            const enginData = enginsExistants.find(
                (e) => e?.immatriculation === value || e?.numeroChassis === value
            ) || enginsExistants[0];

            if (!enginData) {
                // Pas de détail d'engin à reprendre → on garde la saisie en cours, mais on
                // MÉMORISE quand même la confirmation pour les valeurs actuelles (sinon le
                // validateur châssis rebloquerait à « Suivant » alors que l'utilisateur a confirmé).
                const curNoData = [...getEngins()];
                if (curNoData[index]) {
                    curNoData[index].confirmedImmatriculation = curNoData[index].immatriculation || null;
                    curNoData[index].confirmedChassis = curNoData[index].numeroChassis || null;
                    form.setFieldsValue({ engins: curNoData });
                    if (typeof onEnginsUpdate === 'function') onEnginsUpdate(curNoData);
                }
                setOrganismeModal({ visible: false, data: null, field: null, value: null });
                return;
            }

            // Remplir automatiquement les champs du formulaire avec les données de l'engin
            const cur = [...getEngins()];
            cur[index] = {
                ...cur[index],
                // Données d'identification
                immatriculation: enginData.immatriculation || cur[index].immatriculation,
                typeSerie: enginData.typeSerie || cur[index].typeSerie,
                matricule: enginData.matricule || cur[index].matricule,
                numeroSerie: enginData.numeroSerie || cur[index].numeroSerie,
                numeroChassis: enginData.numeroChassis || cur[index].numeroChassis,

                // Données techniques
                marque: enginData.marque || cur[index].marque,
                energie: enginData.energie || cur[index].energie,
                usage: enginData.usage || cur[index].usage,
                volume: enginData.volume || enginData.puissance || cur[index].volume,
                puissance: enginData.puissance || enginData.volume || cur[index].puissance,
                nombrePlace: enginData.nombrePlace || cur[index].nombrePlace,
                genre: enginData.genre || cur[index].genre,
                moteur: enginData.moteur || cur[index].moteur,
                type: enginData.type || cur[index].type,
                carosserie: enginData.carosserie || cur[index].carosserie,

                // Valeurs
                valeurVenale: enginData.valeurVenale || cur[index].valeurVenale,
                valeurDeclaree: enginData.valeurDeclaree || cur[index].valeurDeclaree,
                valeurANeuf: enginData.valeurANeuf || cur[index].valeurANeuf,

                // Date de mise en circulation
                dateMiseEnCirculation: enginData.dateMiseEnCirculation ? dayjs(enginData.dateMiseEnCirculation) : cur[index].dateMiseEnCirculation,
            };

            // Mémorise les valeurs CONFIRMÉES : les validateurs immatriculation/châssis ne
            // re-bloqueront plus tant que ces valeurs ne changent pas. Cas légitime : réassurance
            // d'un engin déjà présent dont le contrat a échu (le châssis existe forcément en base).
            cur[index].confirmedImmatriculation = cur[index].immatriculation || null;
            cur[index].confirmedChassis = cur[index].numeroChassis || null;

            // Mettre à jour le formulaire Ant Design
            form.setFieldsValue({ engins: cur });

            // Notifier le parent
            if (typeof onEnginsUpdate === 'function') onEnginsUpdate(cur);

            // Fermer le modal
            setOrganismeModal({ visible: false, data: null, field: null, value: null });

        } catch (error) {
            console.error("Erreur lors de la récupération des données de l'engin:", error);
            // En cas d'erreur, on ferme quand même le modal
            setOrganismeModal({ visible: false, data: null, field: null, value: null });
        }
    };

    // Gérer l'annulation - l'utilisateur ne veut pas continuer
    const handleOrganismeCancel = () => {
        const { field, value } = organismeModal;

        // Réinitialiser le champ concerné
        const cur = [...getEngins()];
        if (field === 'immatriculation') {
            cur[index].immatriculation = '';
            cur[index].typeSerie = '';
            cur[index].matricule = '';
            cur[index].numeroSerie = '';
            form.setFieldsValue({
                engins: cur
            });
        } else if (field === 'chassis') {
            cur[index].numeroChassis = '';
            form.setFieldsValue({
                engins: cur
            });
        }

        setOrganismeModal({ visible: false, data: null, field: null, value: null });

        // Forcer la revalidation du champ
        if (field === 'immatriculation') {
            form.validateFields([['engins', index, 'typeSerie']]);
        } else if (field === 'chassis') {
            form.validateFields([['engins', index, 'numeroChassis']]);
        }
    };

    const handleEnginChange = (value, field, idx = index) => {
        const cur = [...getEngins()];
        cur[idx] = { ...(cur[idx] || {}), [field]: value };

        // Édition manuelle d'un champ d'identité → l'engin n'est plus « tel qu'importé » :
        // on réactive la vérification châssis/immatriculation en base.
        if (['numeroChassis', 'matricule', 'numeroSerie', 'typeSerie', 'immatriculation'].includes(field)) {
            cur[idx].imported = false;
        }

        // if typeSerie/matricule/numeroSerie affecte immatriculation
        if (['numeroSerie', 'matricule', 'typeSerie'].includes(field)) {
            const v = cur[idx];
            const catCode = categorie?.codeCategorie || categorie;
            v.immatriculation = computeImmatriculation(catCode, v?.typeSerie, v?.numeroSerie, v?.matricule);
        }

        if (field === 'valeurVenale') {
            // reset carrosserie si catégorie changée
            cur[idx].valeurVenale = value;
            cur[idx].valeurDeclaree = value;
            cur[idx].valeurANeuf = value;
        }
        if (field === 'volume') {
            console.log("Volummmmmme " + value, " categorie" + categorie?.codeCategorie);
        }

        if (field === 'puissance') {
            console.log("Puissance " + value);
        }

        if (field === "volume" && !categorie?.codeCategorie?.startsWith("M")) {
            console.log("Volummmmmme " + value);
            cur[idx].puissance = parseInt(value);
        }

        if (field === 'garantiesSupplementaires') {
            cur[idx].garantiesSupplementaires = value;
            // Propage immédiatement le choix (champ Form), sans encore les sous-garanties.
            form.setFieldsValue({ engins: cur });
            if (typeof onEnginsUpdate === 'function') onEnginsUpdate(cur);

            const gtys = (value || [])
                .map((e) => garantiesNonObligatoires.find((el) => el.codeId === e))
                .filter(Boolean);

            // La résolution des sous-garanties est ASYNCHRONE. ⚠ On NE réutilise PAS `cur` (figé au
            // moment de la sélection : il deviendrait périmé si un autre champ — ex. nombre de places —
            // change entre-temps, et écraserait la saisie). On RELIT l'état Form le plus récent et on
            // n'y applique QUE `sousGaranties` pour l'engin concerné.
            axiosInstance
                .post(`/api/sous-garantie/garanties`, JSON.stringify(gtys))
                .then((ress) => {
                    const latest = [...getEngins()];
                    latest[idx] = { ...(latest[idx] || {}), sousGaranties: ress.data || [] };
                    form.setFieldsValue({ engins: latest });
                    if (typeof onEnginsUpdate === 'function') onEnginsUpdate(latest);
                })
                .catch((errr) => {
                    console.log(errr);
                    const latest = [...getEngins()];
                    latest[idx] = { ...(latest[idx] || {}), sousGaranties: [] };
                    form.setFieldsValue({ engins: latest });
                    if (typeof onEnginsUpdate === 'function') onEnginsUpdate(latest);
                });
            return; // propagation finale gérée dans le .then
        }

        if (field === 'dateMiseEnCirculation') {
            const isy = isSameYear(value?.toISOString()) || true;
            setSameYear(isy);
        }

        // update Antd form and parent state
        form.setFieldsValue({ engins: cur });
        if (typeof onEnginsUpdate === 'function') onEnginsUpdate(cur);
    };

    const handleMarqueChange = (value) => {
        const cur = [...getEngins()];
        cur[index] = { ...(cur[index] || {}), marque: value, type: "" };
        form.setFieldsValue({ engins: cur });
        if (typeof onEnginsUpdate === 'function') onEnginsUpdate(cur);
    };

    const persistModele = async (value) => {
        const nom = (value || "").trim().replace(/\s+/g, " ").toUpperCase();
        if (!nom || !selectedMarque?.marqueId) return;
        const exists = (selectedMarque.modeles || []).some(
            (modele) => (modele?.nom || "").toUpperCase() === nom
        );
        if (exists) return;
        try {
            await axiosInstance.post(`/api/vehicule-marques/${selectedMarque.marqueId}/modeles`, { nom });
        } catch (error) {
            console.error("Erreur lors de l'enregistrement du modèle:", error);
        }
    };

    const isTpvCategory = () => {
        const code = (categorie?.codeCategorie || categorie || "").toString().toUpperCase();
        const name = (categorie?.nom || "").toString().toUpperCase();
        const usage = (engin?.usage || "").toString().toUpperCase();
        return (
            code.includes("TPV") ||
            name.includes("TPV") ||
            name.includes("TRANSPORT") ||
            usage === "TPV" ||
            usage.includes("TRANSPORT")
        );
    };

    const allowOptionalImmat = isTpvCategory();

    const typeSerieValidator = (_, value) => {
        if (allowOptionalImmat && engin?.immatriculationFacultative && !value) return Promise.resolve();
        if (!value) return Promise.reject(new Error('Le type de série est requis'));
        return Promise.resolve();
    };

    const matriculeValidator = (_, value) => {
        if (allowOptionalImmat && engin?.immatriculationFacultative && !value) return Promise.resolve();
        if (!value) return Promise.reject(new Error("L'immatriculation est requise"));
        return Promise.resolve();
    };

    const numeroSerieValidator = (_, value) => {
        if (allowOptionalImmat && engin?.immatriculationFacultative) return Promise.resolve();
        if (engin?.typeSerie === 'TG' && !value) return Promise.reject(new Error('N° série requis'));
        return Promise.resolve();
    };

    const handleValeurVenaleBlur = (idx = index) => {
        // placeholder pour calculs éventuels après saisie de la valeur vénale
    };

    const handleShowConducteurModal = (idx = index) => {
        openConducteurModal(idx);
    };

    const removeEngin = (idx = index) => {
        removeVehicle(idx);
    };

    // Validator pour l'immatriculation
    const immatriculationValidator = {
        validator: async (_, value) => {
            // 1. Immatriculation vide → OK
            if (!engin.immatriculation) return Promise.resolve();

            // 2. Mode update : si l'immatriculation n'a pas changé → OK
            if (param?.contratId && engin.oldImmatriculation === engin.immatriculation) {
                return Promise.resolve();
            }

            // 2bis. Existence déjà confirmée par l'utilisateur (modale) pour CETTE immatriculation
            // (ex. contrat échu repris) → ne plus bloquer.
            if (engin.confirmedImmatriculation && engin.confirmedImmatriculation === engin.immatriculation) {
                return Promise.resolve();
            }

            // 3. Vérifier dans la liste des engins du formulaire
            const existsInEngins = checkImmatriculationInEngins(engin.immatriculation, index);
            if (existsInEngins) {
                return Promise.reject(new Error('Cette immatriculation existe déjà dans la liste des engins'));
            }

            // Engin issu d'un import de flotte : pas de vérif en base (perf — cf. châssis).
            if (engin.imported) {
                return Promise.resolve();
            }

            const enginExistant = await checkOrganismeContrat(engin.immatriculation);
            console.log('enginExistant', enginExistant);
            if (enginExistant) {
                const differentOrganisme = enginExistant?.organisme?.code !== LOGGED_USER?.profil?.organisme?.code;
                // Contrat existant échu ? (date d'échéance dépassée)
                const echeance = enginExistant?.dateEcheance ? dayjs(enginExistant.dateEcheance) : null;
                const estEchu = echeance ? echeance.isBefore(dayjs()) : false;
                // Poursuite possible si : autre organisme OU contrat échu. Sinon (même organisme + actif) : bloqué.
                const peutPoursuivre = differentOrganisme || estEchu;

                // On affiche TOUJOURS la modale (information complète) ; le bouton « Continuer » est
                // conditionné. Le champ reste valide → la décision se prend dans la modale.
                setOrganismeModal({
                    visible: true,
                    data: enginExistant,
                    field: 'immatriculation',
                    value: engin.immatriculation,
                    echu: !differentOrganisme && estEchu,
                    blocked: !peutPoursuivre,
                });
                return Promise.resolve();
            }

            return Promise.resolve();
        }
    };

    // Validator pour le nombre de places
    const nombrePlaceValidator = {
        validator: (_, value) => {
            if (!value || !categorie) return Promise.resolve();

            const min = categorie.nombrePlaceMin || 0;
            const max = categorie.nombrePlaceMax || Infinity;

            if (value < min) {
                return Promise.reject(new Error(`Le nombre de places minimum est ${min} pour cette catégorie`));
            }
            if (value > max) {
                return Promise.reject(new Error(`Le nombre de places maximum est ${max} pour cette catégorie`));
            }

            return Promise.resolve();
        }
    };

    // Validator pour le châssis
    const chassisValidator = {
        validator: async (_, value) => {
            if (!value) return Promise.resolve();


            // 2. Mode update : si le numero de chassis n'a pas changé → OK
            if (param?.contratId && engin.oldNumeroChassis === engin.numeroChassis) {
                return Promise.resolve();
            }

            // 2bis. Existence déjà confirmée par l'utilisateur (modale) pour CE châssis
            // (ex. réassurance d'un engin au contrat échu — le châssis existe forcément en base)
            // → ne plus bloquer.
            if (engin.confirmedChassis && engin.confirmedChassis === engin.numeroChassis) {
                return Promise.resolve();
            }

            const existsInEngins = checkChassisInEngins(value, index);
            if (existsInEngins) {
                return Promise.reject(new Error('Ce numéro de châssis existe déjà dans la liste des engins'));
            }

            // Engin issu d'un import de flotte : on NE refait PAS la vérification en base
            // (1 appel API/engin + cascade de re-renders → page qui rame/crashe sur une grosse
            // flotte). Le doublon DANS la liste reste contrôlé ci-dessus (synchrone). Un édit
            // manuel du châssis efface ce drapeau (cf. handleEnginChange) et réactive la vérif.
            if (engin.imported) {
                return Promise.resolve();
            }

            const enginExistant = await checkChassisInDB(value);
            if (enginExistant) {
                // Vérifier l'organisme du contrat
                const organismeInfo = await checkOrganismeContrat(enginExistant.immatriculation || enginExistant.id || value);
                if (organismeInfo) {
                    const echeance = organismeInfo?.dateEcheance ? dayjs(organismeInfo.dateEcheance) : null;
                    const estEchu = echeance ? echeance.isBefore(dayjs()) : false;
                    const autreOrganisme = organismeInfo?.autreOrganisme
                        || (organismeInfo?.organisme?.code && organismeInfo.organisme.code !== LOGGED_USER?.profil?.organisme?.code);
                    const peutPoursuivre = autreOrganisme || estEchu;

                    setOrganismeModal({
                        visible: true,
                        data: organismeInfo,
                        field: 'chassis',
                        value: value,
                        echu: !autreOrganisme && estEchu,
                        blocked: !peutPoursuivre,
                    });
                    return Promise.resolve();
                }
                // Pas d'info contrat mais châssis présent en base : on N'EMPÊCHE PAS. On informe
                // (toast dédupliqué — évite une avalanche de popups sur une flotte) et on
                // « whitelise » ce châssis pour ne pas rebloquer à « Suivant ». L'utilisateur garde
                // la main : il peut retirer l'engin manuellement s'il ne veut pas le réassurer.
                const curChassis = [...getEngins()];
                if (curChassis[index]) {
                    curChassis[index].confirmedChassis = curChassis[index].numeroChassis || value;
                    form.setFieldsValue({ engins: curChassis });
                    if (typeof onEnginsUpdate === 'function') onEnginsUpdate(curChassis);
                }
                message.info({
                    content: `Châssis « ${value} » déjà présent en base — conservé. Retirez l'engin si besoin.`,
                    key: 'chassis-exists-info',
                });
                return Promise.resolve();
            }

            return Promise.resolve();
        }
    };

    const isMoto = categorie?.nom === 'MOTO';

    // Genre & carrosserie sont proposés pour les motos ET les TPV (transport public).
    const showGenreCarosserie = isMoto || allowOptionalImmat;

    // Ils ne sont OBLIGATOIRES que pour les catégories moto ("M" / "MO").
    // Pour toute autre catégorie (TPV, etc.) ils restent facultatifs.
    const catCode = (categorie?.codeCategorie || categorie || "").toString().toUpperCase();
    const genreCarosserieRequired = catCode === "M" || catCode === "MO";

    return (
        <>
            <Card
                key={index}
                className="mb-3"
                size="small"
                title={`Engin N°${index + 1}`}
                extra={
                    (getEngins().length > 1) && (
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeEngin(index)}
                        >
                            Supprimer
                        </Button>
                    )
                }
            >
                <Row gutter={16}>
                    {/* Immatriculation */}
                    <Col span={8}>
                        <Form.Item
                            label={
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Immatriculation {allowOptionalImmat && engin?.immatriculationFacultative ? "(Facultatif)" : "*"}</span>
                                </div>
                            }
                            name={['engins', index, 'typeSerie']}
                            rules={[{ validator: typeSerieValidator }, immatriculationValidator]}
                            validateTrigger={['onChange', 'onBlur']}
                        >
                            <Input.Group compact>
                                <Select
                                    value={engin.typeSerie}
                                    onChange={(val) => handleEnginChange(val, 'typeSerie', index)}
                                    style={{ width: '20%' }}
                                >
                                    <Option value="TG">TG</Option>
                                    <Option value="Autre">Autre</Option>
                                </Select>
                                {/* matricule */}
                                <Form.Item name={['engins', index, 'matricule']} noStyle rules={[{ validator: matriculeValidator }]}>
                                    <Input
                                        value={engin.matricule}
                                        onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'matricule', index)}
                                        style={{ width: '50%' }}
                                        placeholder="Immatriculation"
                                    />
                                </Form.Item>
                                {engin?.typeSerie === 'TG' && (
                                    <Form.Item name={['engins', index, 'numeroSerie']} noStyle rules={[{ validator: numeroSerieValidator }]}>
                                        <Input
                                            value={engin.numeroSerie}
                                            onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'numeroSerie', index)}
                                            style={{ width: '30%' }}
                                            placeholder="N° série"
                                        />
                                    </Form.Item>
                                )}
                            </Input.Group>
                        </Form.Item>
                    </Col>

                    {/* Marque */}
                    <Col span={8}>
                        <Form.Item label="Marque" name={['engins', index, 'marque']} rules={[{ required: true, message: 'La marque est requise' }]}>
                            <Select
                                value={engin.marque}
                                onChange={handleMarqueChange}
                                placeholder="Sélectionner la marque"
                                showSearch
                                optionFilterProp="label"
                                options={normalizedMarques?.map((marque) => ({
                                    label: marque.nom,
                                    value: marque.nom,
                                    key: marque.marqueId || marque.nom
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    {/* Type */}
                    <Col span={8}>
                        <Form.Item label="Type" name={['engins', index, 'type']} rules={[{ required: true, message: 'Type requis' }]}>
                            <AutoComplete
                                value={engin.type}
                                onChange={(value) => handleEnginChange((value || "").toUpperCase(), 'type', index)}
                                onSelect={(value) => handleEnginChange((value || "").toUpperCase(), 'type', index)}
                                onBlur={() => persistModele(form.getFieldValue(['engins', index, 'type']))}
                                options={modeleOptions}
                                placeholder={selectedMarque ? "Sélectionner ou saisir le type" : "Sélectionner d'abord la marque"}
                                filterOption={(inputValue, option) =>
                                    (option?.value || "").toUpperCase().includes((inputValue || "").toUpperCase())
                                }
                                disabled={!selectedMarque}
                            />
                        </Form.Item>
                    </Col>

                    {/* Usage */}
                    <Col span={8}>
                        <Form.Item label="Usage" name={['engins', index, 'usage']} rules={[{ required: true, message: "L'usage est requis" }]}>
                            <Select
                                value={engin.usage}
                                onChange={(val) => handleEnginChange(val, 'usage', index)}
                                disabled={!isMoto}
                            >
                                <Option value="PRIVE">PRIVE</Option>
                                <Option value="TAXI">TAXI</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Volume */}
                    <Col span={8}>
                        <Form.Item
                            label={isMoto ? "Volume (cm³) / Puissance" : "Puissance (CV)"}
                            name={['engins', index, 'volume']}
                            rules={[{ required: true, message: 'Le volume est requis' }]}>
                            <InputNumber
                                value={engin.volume}
                                onChange={(val) => handleEnginChange(val, 'volume', index)}
                                style={{ width: '100%' }}
                                placeholder={isMoto ? "Volume / Puissance" : "Puissance"}
                            />
                        </Form.Item>
                    </Col>

                    {/* Nombre de places */}
                    <Col span={8}>
                        <Form.Item label="Nombre de places" name={['engins', index, 'nombrePlace']} rules={[{ required: true, message: 'Le nombre de places est requis' }, nombrePlaceValidator]}>
                            <InputNumber
                                value={engin.nombrePlace}
                                onChange={(val) => handleEnginChange(val, 'nombrePlace', index)}
                                style={{ width: '100%' }}
                                placeholder="Nombre de places"
                                min={categorie?.nombrePlaceMin || 1}
                                max={categorie?.nombrePlaceMax || 100}
                            />
                        </Form.Item>
                    </Col>

                    {/* Genre */}
                    {showGenreCarosserie ? <Col span={8}>
                        <Form.Item label={genreCarosserieRequired ? "Genre" : "Genre (Facultatif)"} name={['engins', index, 'genre']} rules={[{ required: genreCarosserieRequired, message: 'Le genre est requis' }]}>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                value={engin.genre}
                                onChange={(val) => handleEnginChange(val, 'genre', index)}
                                placeholder="Sélectionner le genre"
                                options={genres?.map((g) => ({ label: g.libelleParametre, value: g.libelleParametre }))}
                            />
                        </Form.Item>
                    </Col> : null}

                    {/* N° Moteur */}
                    <Col span={8}>
                        <Form.Item label="N° Moteur" name={['engins', index, 'moteur']} rules={[{ required: true, message: 'N° moteur requis' }]}>
                            <Input
                                value={engin.moteur}
                                onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'moteur', index)}
                                placeholder="Numéro de moteur"
                            />
                        </Form.Item>
                    </Col>


                    {/* Énergie */}
                    <Col span={8}>
                        <Form.Item label="Énergie" name={['engins', index, 'energie']} rules={[{ required: true, message: 'L\'énergie est requise' }]}>
                            <Select
                                value={engin.energie}
                                onChange={(val) => handleEnginChange(val, 'energie', index)}
                            >
                                <Option value="ESSENCE">ESSENCE</Option>
                                <Option value="DIESEL">DIESEL</Option>
                                <Option value="ELECTRIQUE">ÉLECTRIQUE</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* N° Châssis */}
                    <Col span={8}>
                        <Form.Item label="N° Châssis" name={['engins', index, 'numeroChassis']} rules={[{ required: true, message: 'N° châssis requis' }, chassisValidator]} validateTrigger={['onChange', 'onBlur']}>
                            <Input
                                value={engin.numeroChassis}
                                onChange={(e) => handleEnginChange(e.target.value.toUpperCase(), 'numeroChassis', index)}
                                placeholder="Numéro de châssis"
                            />
                        </Form.Item>
                    </Col>

                    {/* Carrosserie - proposée pour MOTO et TPV (obligatoire uniquement pour MOTO) */}
                    {showGenreCarosserie ? <Col span={8}>
                        <Form.Item label={genreCarosserieRequired ? "Carosserie" : "Carosserie (Facultatif)"} name={['engins', index, 'carosserie']} rules={[{ required: genreCarosserieRequired, message: 'La carrosserie est requise' }]}>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                value={engin.carosserie}
                                onChange={(val) => handleEnginChange(val, 'carosserie', index)}
                                placeholder="Sélectionner la carrosserie"
                                options={carosseries?.map((c) => ({ label: c.libelleParametre, value: c.libelleParametre }))}
                            />
                        </Form.Item>
                    </Col> : null}

                    {/* Valeur vénale */}
                    <Col span={8}>
                        <Form.Item label="Valeur vénale" name={['engins', index, 'valeurVenale']} rules={[{ required: true, message: 'Valeur vénale requise' }]}>
                            <InputNumber
                                value={engin.valeurVenale}
                                onChange={(val) => handleEnginChange(val, 'valeurVenale', index)}
                                onBlur={() => handleValeurVenaleBlur(index)}
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                parser={(value) => value.replace(/\s/g, '')}
                                placeholder="Valeur vénale"
                            />
                        </Form.Item>
                    </Col>

                    {/* Valeur déclarée */}
                    <Col span={8}>
                        <Form.Item label="Valeur déclarée" name={['engins', index, 'valeurDeclaree']} rules={[{ required: true, message: 'Valeur déclarée requise' }]}>
                            <InputNumber
                                value={engin.valeurDeclaree}
                                onChange={(val) => handleEnginChange(val, 'valeurDeclaree', index)}
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                parser={(value) => value.replace(/\s/g, '')}
                                placeholder="Valeur Déclarée"
                            />
                        </Form.Item>
                    </Col>

                    {/* Valeur à neuf */}
                    <Col span={8}>
                        <Form.Item label="Valeur à neuf" name={['engins', index, 'valeurANeuf']} rules={[{ required: true, message: 'Valeur à neuf requise' }]}>
                            <InputNumber
                                value={engin.valeurANeuf}
                                onChange={(val) => handleEnginChange(val, 'valeurANeuf', index)}
                                style={{ width: '100%' }}
                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                parser={(value) => value.replace(/\s/g, '')}
                                placeholder="Valeur à neuf"
                            />
                        </Form.Item>
                    </Col>

                    {/* Date de mise en circulation */}
                    <Col span={8}>
                        <Form.Item
                            label="Année de mise en circulation"
                            name={['engins', index, 'dateMiseEnCirculation']}
                            rules={[{ required: true, message: 'Année requise' }]}
                        >
                            <DatePicker
                                value={engin?.dateMiseEnCirculation ? dayjs(engin.dateMiseEnCirculation) : null}
                                onChange={(date) => handleEnginChange(date ?? null, 'dateMiseEnCirculation', index)}
                                picker="year"
                                style={{ width: '100%' }}
                                placeholder="Sélectionner l'année"
                                disabledDate={(current) => current && current > dayjs()}
                            />
                        </Form.Item>
                    </Col>

                    {/* Garanties supplémentaires */}
                    <Col span={8}>
                        <Form.Item label="Garanties supplémentaires" name={['engins', index, 'garantiesSupplementaires']}>
                            <Select
                                mode="multiple"
                                value={engin.garantiesSupplementaires}
                                onChange={(val) => handleEnginChange(val, 'garantiesSupplementaires', index)}
                                placeholder="Sélectionner les garanties"
                            >
                                {garantiesNonObligatoires.map((gar, id) => (
                                    <Option key={id} value={gar.codeId}>
                                        {gar.nom}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Conducteur habituel */}
                    <Col span={24}>
                        {engin?.useSouscripteur === false ? (
                            <Card size="small" className="bg-light">
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Text strong>Conducteur habituel : </Text>
                                        <Text>
                                            {engin.personne?.nom} {engin.personne?.prenom}
                                        </Text>
                                    </Col>
                                    <Col>
                                        <Button
                                            size="small"
                                            onClick={() => handleShowConducteurModal(index)}
                                            className="me-2"
                                        >
                                            Modifier
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            onClick={() => {
                                                // retirer conducteur: remettre useSouscripteur true et vider personne
                                                handleEnginChange(true, 'useSouscripteur', index);
                                                handleEnginChange({}, 'personne', index);
                                            }}
                                        >
                                            Retirer
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>
                        ) : (
                            <Alert
                                message="Par défaut, le souscripteur du contrat sera le conducteur habituel"
                                type="info"
                                action={
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<UserOutlined />}
                                        onClick={() => handleShowConducteurModal(index)}
                                    >
                                        Préciser un conducteur différent
                                    </Button>
                                }
                            />
                        )}
                    </Col>
                </Row>
            </Card>

            {/* Modal d'information : engin déjà existant (autre organisme, contrat échu, ou contrat actif) */}
            <Modal
                title={
                    <span>
                        <WarningOutlined style={{ color: organismeModal.blocked ? '#cf1322' : '#faad14', marginRight: 8 }} />
                        {organismeModal.chassisOnly
                            ? "Châssis déjà présent en base"
                            : organismeModal.blocked
                                ? "Engin déjà couvert par un contrat ACTIF"
                                : organismeModal.echu
                                    ? "Engin déjà assuré chez vous — contrat échu"
                                    : "Engin assuré dans un autre organisme"}
                    </span>
                }
                open={organismeModal.visible}
                onOk={handleOrganismeConfirm}
                onCancel={handleOrganismeCancel}
                okText="Continuer"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
                width={600}
            >
                <Alert
                    message={organismeModal.chassisOnly ? "Information" : organismeModal.blocked ? "Engin déjà couvert par un contrat actif" : "Attention"}
                    description={
                        <div>
                            <p>
                                Cet engin ({organismeModal.field === 'immatriculation' ? 'immatriculation' : 'châssis'}: <strong>{organismeModal.value}</strong>)
                                {organismeModal.chassisOnly
                                    ? " existe déjà dans la base de données."
                                    : organismeModal.blocked
                                        ? " est déjà couvert par un contrat ENCORE ACTIF de votre organisme :"
                                        : organismeModal.echu
                                            ? " est déjà présent dans votre organisme, mais son contrat a échu :"
                                            : " était assuré dans un autre organisme :"}
                            </p>
                            {!organismeModal.chassisOnly && (
                                <ul style={{ marginTop: 10, marginBottom: 10 }}>
                                    <li><strong>Organisme :</strong> {organismeModal?.data?.organisme?.raisonSociale}</li>
                                    <li><strong>N° police :</strong> {organismeModal?.data?.numeroPoliceP || organismeModal?.data?.numeroPoliceM || 'N/A'}</li>
                                    <li><strong>Période :</strong> Du {organismeModal?.data?.dateEffet ? dayjs(organismeModal?.data?.dateEffet).format('DD/MM/YYYY') : 'N/A'} au {organismeModal?.data?.dateEcheance ? dayjs(organismeModal?.data?.dateEcheance).format('DD/MM/YYYY') : 'N/A'}</li>
                                </ul>
                            )}
                            <p style={{ marginTop: 15 }}>
                                {organismeModal.chassisOnly
                                    ? "Si c'est un engin déjà connu (réassurance, flotte déjà saisie), vous pouvez continuer en cliquant sur « Continuer »."
                                    : organismeModal.blocked
                                        ? "Le contrat de cet engin est toujours en cours. Vous pouvez tout de même poursuivre l'affaire nouvelle en cliquant sur « Continuer » — assurez-vous toutefois qu'il ne s'agit pas d'une double assurance involontaire. Les informations de l'engin seront pré-remplies automatiquement."
                                        : organismeModal.echu
                                            ? "Le contrat précédent étant échu, vous pouvez poursuivre. Les informations de l'engin seront pré-remplies automatiquement. Confirmer ?"
                                            : "Voulez-vous vraiment continuer ? Les informations de l'engin seront pré-remplies automatiquement."}
                            </p>
                        </div>
                    }
                    type={organismeModal.chassisOnly ? "info" : organismeModal.blocked ? "error" : "warning"}
                    showIcon
                />
            </Modal>
        </>
    );
};

export default memo(EnginForm);
